import type { Subcategory } from '~/entities/categories'

export interface FacetOption {
  label: string
  displayLabel: string
  /**
   * Every category reachable through this option is deprecated, so there is nothing
   * selectable behind it. Deprecation is a property of the leaf category, not of a
   * facet value — the same reason can be retired under one outcome and live under
   * another — so it has to be reduced over the reachable leaves.
   */
  isDeprecated: boolean
}

/**
 * Collapses subcategories into the distinct values of one facet, in first-occurrence
 * order, flagging the options that lead only to deprecated categories.
 */
export function getFacetOptions({
  subcategories,
  selectFacet,
}: {
  subcategories: Subcategory[]
  selectFacet: (subcategory: Subcategory) => { label: string; displayLabel: string }
}): FacetOption[] {
  const leavesByLabel = new Map<string, { displayLabel: string; subcategories: Subcategory[] }>()

  for (const subcategory of subcategories) {
    const { label, displayLabel } = selectFacet(subcategory)
    const group = leavesByLabel.get(label)

    if (group) {
      group.subcategories.push(subcategory)
    } else {
      leavesByLabel.set(label, { displayLabel, subcategories: [subcategory] })
    }
  }

  return [...leavesByLabel].map(([label, group]) => ({
    label,
    displayLabel: group.displayLabel,
    isDeprecated: group.subcategories.every(({ deprecated }) => deprecated),
  }))
}
